/**
 * ======================================================
 * 🏛 APP — ROOT APPLICATION SHELL
 * ======================================================
 *
 * 🎯 RESPONSABILITÉ
 * ------------------------------------------------------
 * - Monter le layout principal
 * - Monter les overlays globaux (modals)
 *
 * 👉 Ne contient PAS :
 * - logique métier
 * - logique auth
 * - logique registration
 *
 * App = Shell global
 */

import { Component, useEffect } from "react";

import RootLayout from "./layouts/RootLayout";
import AuthModal from "./components/AuthModal";
import { useAuthUI } from "./core/auth/AuthUIContext";
import ProfileCompletionGate from "./features/profile/ProfileCompletionGate";
import TripCostCalculatorPage from "./features/tripCost/TripCostCalculatorPage";
import PublicOrganizerEventsPage from "./features/publicShare/PublicOrganizerEventsPage";
import { matchOrganizerEventsSharePath } from "./features/publicShare/organizerEventsShareRoute";
import OrganizerInvitationPage from "./features/organizerInvitation/OrganizerInvitationPage";
import { matchOrganizerInvitationPath } from "./features/organizerInvitation/organizerInvitationRoute";
import MemberCertificationPage from "./features/certifications/MemberCertificationPage";
import { matchMemberCertificationPath } from "./features/certifications/memberCertificationRoute";

const CHUNK_ERROR_PATTERN =
  /ChunkLoadError|Loading chunk|CSS_CHUNK_LOAD_FAILED|dynamically imported module|Failed to fetch dynamically imported module/i;

function getErrorMessage(error) {
  if (!error) return "";
  if (typeof error === "string") return error;
  return String(error.message || error.name || error);
}

function isChunkLoadError(error) {
  return CHUNK_ERROR_PATTERN.test(getErrorMessage(error));
}

function getCurrentMainScriptKey() {
  if (typeof document === "undefined") {
    return "unknown";
  }

  const mainScript = Array.from(document.scripts)
    .map((script) => script.src || "")
    .find((src) => /\/static\/js\/main\.[^/]+\.js$/i.test(src));

  return mainScript || window.location.href;
}

function reloadOnceForChunkError(error) {
  if (!isChunkLoadError(error) || typeof window === "undefined") {
    return false;
  }

  const reloadKey = `karango:chunk-reload:${getCurrentMainScriptKey()}`;

  try {
    if (window.sessionStorage.getItem(reloadKey) === "1") {
      return false;
    }
    window.sessionStorage.setItem(reloadKey, "1");
  } catch {
    // If storage is blocked, still try a normal reload.
  }

  window.location.reload();
  return true;
}

function ChunkLoadRecoveryListener() {
  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      if (reloadOnceForChunkError(event.reason)) {
        event.preventDefault?.();
      }
    };

    const handleError = (event) => {
      reloadOnceForChunkError(event.error || event.message);
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("error", handleError);

    return () => {
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
      window.removeEventListener("error", handleError);
    };
  }, []);

  return null;
}

class ChunkLoadBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    reloadOnceForChunkError(error);
  }

  render() {
    const { error } = this.state;

    if (!error) {
      return this.props.children;
    }

    if (!isChunkLoadError(error)) {
      throw error;
    }

    return (
      <main
        style={{
          minHeight: "100dvh",
          display: "grid",
          placeItems: "center",
          padding: 24,
          background: "#050914",
          color: "#f8fbff",
          fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          textAlign: "center",
        }}
      >
        <section style={{ maxWidth: 420 }}>
          <h1 style={{ margin: "0 0 10px", fontSize: 28 }}>Mise à jour disponible</h1>
          <p style={{ margin: "0 0 18px", color: "rgba(226, 232, 240, 0.76)", lineHeight: 1.6 }}>
            Recharge la page pour ouvrir la dernière version de Karango.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              minHeight: 44,
              padding: "0 18px",
              borderRadius: 999,
              border: "1px solid rgba(191, 219, 254, 0.28)",
              background: "rgba(59, 130, 246, 0.22)",
              color: "#f8fbff",
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            Recharger
          </button>
        </section>
      </main>
    );
  }
}

export default function App() {
  /**
   * 🎛 État UI global
   * ------------------------------------------------------
   * Provient du AuthUIContext
   */
  const { isAuthModalOpen } = useAuthUI();

  if (typeof window !== "undefined" && window.location.pathname === "/calculateur-trajet") {
    return (
      <ChunkLoadBoundary>
        <ChunkLoadRecoveryListener />
        <TripCostCalculatorPage />
      </ChunkLoadBoundary>
    );
  }

  if (typeof window !== "undefined") {
    const memberCertificationId = matchMemberCertificationPath(window.location.pathname);
    if (memberCertificationId) {
      return (
        <ChunkLoadBoundary>
          <ChunkLoadRecoveryListener />
          <MemberCertificationPage requestId={memberCertificationId} />
          {isAuthModalOpen && <AuthModal />}
        </ChunkLoadBoundary>
      );
    }

    const organizerInvitationToken = matchOrganizerInvitationPath(window.location.pathname);
    if (organizerInvitationToken) {
      return (
        <ChunkLoadBoundary>
          <ChunkLoadRecoveryListener />
          <OrganizerInvitationPage token={organizerInvitationToken} />
          {isAuthModalOpen && <AuthModal />}
        </ChunkLoadBoundary>
      );
    }

    const organizerEventsShareToken = matchOrganizerEventsSharePath(window.location.pathname);
    if (organizerEventsShareToken) {
      return (
        <ChunkLoadBoundary>
          <ChunkLoadRecoveryListener />
          <PublicOrganizerEventsPage token={organizerEventsShareToken} />
          {/* Cette page court-circuite RootLayout : sans ce rendu explicite,
              openAuthModal() change bien l'etat isAuthModalOpen mais aucune
              modale n'existe dans l'arbre pour le refleter. */}
          {isAuthModalOpen && <AuthModal />}
        </ChunkLoadBoundary>
      );
    }
  }

  return (
    <ChunkLoadBoundary>
      <ChunkLoadRecoveryListener />

      {/* Layout principal */}
      <RootLayout />

      {/* Modal globale */}
      {isAuthModalOpen && <AuthModal />}

      <ProfileCompletionGate />
    </ChunkLoadBoundary>
  );
}
